import { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { KNORIA_LOGO_URL } from "../components/KnoriaLogo";
import {
  Play, Pause, SpeakerHigh, SpeakerSlash, CornersOut,
  SkipBack, SkipForward, Gauge, DownloadSimple, Clock,
  CircleNotch, LinkBreak,
  Waveform, Brain, FileText, MicrophoneStage, CheckCircle, Translate,
  MagnifyingGlass, X, Question, ListChecks, Microphone, ShieldWarning, Lightbulb
} from "@phosphor-icons/react";
import { useLang } from "../contexts/LanguageContext";

import { API_BASE as API } from "../lib/apiBase";

function formatTime(seconds) {
  if (!seconds || isNaN(seconds)) return "0:00";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

const TYPE_ICONS = { qa: Question, procedure: ListChecks, script: Microphone, objection: ShieldWarning, rebuttal: ShieldWarning, concept: Lightbulb };
const TYPE_COLORS = {
  qa: { bg: "rgba(59,130,246,0.15)", color: "#3b82f6", border: "rgba(59,130,246,0.3)" },
  procedure: { bg: "rgba(34,197,94,0.15)", color: "#22c55e", border: "rgba(34,197,94,0.3)" },
  script: { bg: "rgba(234,179,8,0.15)", color: "#eab308", border: "rgba(234,179,8,0.3)" },
  objection: { bg: "rgba(239,68,68,0.15)", color: "#ef4444", border: "rgba(239,68,68,0.3)" },
  rebuttal: { bg: "rgba(168,85,247,0.15)", color: "#a855f7", border: "rgba(168,85,247,0.3)" },
  concept: { bg: "rgba(99,102,241,0.15)", color: "#6366f1", border: "rgba(99,102,241,0.3)" },
};
const SPEAKER_COLORS = ["#3b82f6", "#22c55e", "#eab308", "#ef4444", "#a855f7", "#ec4899", "#14b8a6", "#f97316"];

export default function SharedVideoPage() {
  const { token } = useParams();
  const { lang, t } = useLang();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isBuffering, setIsBuffering] = useState(true);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);

  const [activePanel, setActivePanel] = useState("transcript");
  const [transcriptSearch, setTranscriptSearch] = useState("");

  const videoRef = useRef(null);
  const playerRef = useRef(null);
  const progressRef = useRef(null);
  const controlsTimeout = useRef(null);
  const transcriptContainerRef = useRef(null);
  const activeSegmentRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data: res } = await axios.get(`${API}/api/share/${token}`);
        setData(res);
      } catch (err) {
        const status = err.response?.status;
        if (status === 410) setError("expired");
        else if (status === 404) setError("invalid");
        else setError("error");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const togglePlay = useCallback(() => {
    if (!videoRef.current) return;
    if (videoRef.current.paused) videoRef.current.play().catch(() => {});
    else videoRef.current.pause();
  }, []);

  const skip = useCallback((d) => {
    if (videoRef.current) videoRef.current.currentTime = Math.max(0, Math.min(duration, videoRef.current.currentTime + d));
  }, [duration]);

  const seekTo = useCallback((time) => {
    if (videoRef.current) { videoRef.current.currentTime = time; videoRef.current.play().catch(() => {}); }
  }, []);

  const toggleMute = useCallback(() => {
    if (videoRef.current) { videoRef.current.muted = !videoRef.current.muted; setIsMuted(!isMuted); }
  }, [isMuted]);

  const showCtrl = useCallback(() => {
    setControlsVisible(true);
    if (controlsTimeout.current) clearTimeout(controlsTimeout.current);
    controlsTimeout.current = setTimeout(() => { if (isPlaying) setControlsVisible(false); }, 3000);
  }, [isPlaying]);

  const handleProgressClick = (e) => {
    if (!progressRef.current || !duration || !videoRef.current) return;
    const rect = progressRef.current.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    videoRef.current.currentTime = pct * duration;
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  // Auto-scroll transcript
  useEffect(() => {
    if (activeSegmentRef.current && transcriptContainerRef.current) {
      const container = transcriptContainerRef.current;
      const el = activeSegmentRef.current;
      const top = el.offsetTop - container.offsetTop - container.clientHeight / 3;
      container.scrollTo({ top, behavior: "smooth" });
    }
  }, [currentTime]);

  // Expiration countdown
  const [timeLeft, setTimeLeft] = useState("");
  useEffect(() => {
    if (!data?.expires_at) return;
    const tick = () => {
      const diff = new Date(data.expires_at) - new Date();
      if (diff <= 0) { setTimeLeft("Expire"); return; }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      setTimeLeft(h > 0 ? `${h}h ${m}min` : `${m}min`);
    };
    tick();
    const iv = setInterval(tick, 60000);
    return () => clearInterval(iv);
  }, [data]);

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <CircleNotch size={40} className="animate-spin text-blue-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center max-w-md px-6">
          <div className="w-20 h-20 rounded-full bg-zinc-800 flex items-center justify-center mx-auto mb-6">
            {error === "expired" ? <Clock size={40} className="text-amber-400" /> : <LinkBreak size={40} className="text-red-400" />}
          </div>
          <h1 className="text-2xl font-bold text-white mb-2 font-['Outfit']">
            {error === "expired" ? "Lien expire" : "Lien invalide"}
          </h1>
          <p className="text-zinc-400 text-sm">
            {error === "expired"
              ? "Ce lien de partage a expire. Demandez un nouveau lien au propriétaire de la video."
              : "Ce lien de partage n'existe pas ou a ete revoque."}
          </p>
        </div>
      </div>
    );
  }

  const { video, shared_by, pipeline, segments, knowledge_units } = data;

  // Speaker map
  const speakerMap = {};
  let speakerIdx = 0;
  if (segments) {
    segments.forEach((seg) => {
      if (seg.speaker && !(seg.speaker in speakerMap)) {
        speakerMap[seg.speaker] = SPEAKER_COLORS[speakerIdx % SPEAKER_COLORS.length];
        speakerIdx++;
      }
    });
  }

  const getActiveSegmentIdx = () => {
    if (!segments) return -1;
    return segments.findIndex((seg) => currentTime >= seg.start && currentTime <= seg.end);
  };
  const activeIdx = getActiveSegmentIdx();

  const filteredSegments = segments?.filter((seg) => {
    if (!transcriptSearch) return true;
    return seg.text?.toLowerCase().includes(transcriptSearch.toLowerCase()) ||
      seg.speaker?.toLowerCase().includes(transcriptSearch.toLowerCase());
  }) || [];

  return (
    <div className="min-h-screen bg-zinc-950" data-testid="shared-video-page">
      {/* Top Bar */}
      <div className="border-b border-zinc-800 bg-zinc-900/80 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={KNORIA_LOGO_URL} alt="KNORIA" className="h-8 object-contain" />
            <div className="h-5 w-px bg-zinc-700" />
            <span className="text-xs text-zinc-400">Video partagee par <span className="text-zinc-200 font-medium">{shared_by}</span></span>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5 text-xs text-zinc-400" data-testid="share-timer">
              <Clock size={14} />
              <span>Expire dans {timeLeft}</span>
            </div>
            <a
              href={`${API}/api/share/${token}/download`}
              className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-500 transition-colors"
              data-testid="share-download-btn"
            >
              <DownloadSimple size={16} weight="bold" />
              Télécharger
            </a>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 xl:grid-cols-5 gap-4">
          {/* Video Player (3/5) */}
          <div className="xl:col-span-3 space-y-3">
            <div
              ref={playerRef}
              className="relative rounded-xl overflow-hidden bg-black group"
              style={{ aspectRatio: "16/9" }}
              onMouseMove={showCtrl}
              onMouseLeave={() => { if (isPlaying) setControlsVisible(false); }}
              onDoubleClick={() => { if (document.fullscreenElement) document.exitFullscreen(); else playerRef.current?.requestFullscreen(); }}
              data-testid="shared-video-container"
            >
              <video
                ref={videoRef}
                className="w-full h-full object-contain cursor-pointer"
                onTimeUpdate={() => { if (videoRef.current) { setCurrentTime(videoRef.current.currentTime); if (videoRef.current.duration && !isNaN(videoRef.current.duration)) setDuration(videoRef.current.duration); } }}
                onCanPlay={() => setIsBuffering(false)}
                onWaiting={() => setIsBuffering(true)}
                onLoadedMetadata={() => { if (videoRef.current?.duration && !isNaN(videoRef.current.duration)) { setDuration(videoRef.current.duration); setIsBuffering(false); } }}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onClick={togglePlay}
                preload="metadata"
                playsInline
                data-testid="shared-video-player"
              >
                <source src={`${API}/api/share/${token}/stream`} type="video/mp4" />
              </video>

              {isBuffering && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/60 pointer-events-none">
                  <CircleNotch size={48} className="text-white animate-spin" />
                </div>
              )}

              {!isPlaying && !isBuffering && (
                <div className="absolute inset-0 flex items-center justify-center cursor-pointer" onClick={togglePlay}>
                  <div className="w-20 h-20 rounded-full bg-blue-600/90 backdrop-blur-sm flex items-center justify-center shadow-2xl hover:scale-110 transition-transform">
                    <Play size={36} weight="fill" className="text-white ml-1" />
                  </div>
                </div>
              )}

              {/* Controls */}
              <div className={`absolute bottom-0 left-0 right-0 transition-all duration-300 ${controlsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2 pointer-events-none"}`}
                   style={{ background: "linear-gradient(transparent, rgba(0,0,0,0.85))" }}>
                <div ref={progressRef} className="relative h-8 px-4 flex items-end cursor-pointer group/p" onClick={handleProgressClick}>
                  <div className="w-full h-1 group-hover/p:h-1.5 rounded-full bg-white/20 transition-all relative mb-2">
                    <div className="absolute top-0 left-0 h-full rounded-full bg-blue-500 transition-all" style={{ width: `${progress}%` }} />
                    <div className="absolute top-1/2 -translate-y-1/2 w-3.5 h-3.5 rounded-full bg-white shadow-lg opacity-0 group-hover/p:opacity-100 transition-opacity" style={{ left: `calc(${progress}% - 7px)` }} />
                  </div>
                </div>
                <div className="flex items-center gap-1 px-4 pb-3">
                  <button onClick={togglePlay} className="w-9 h-9 flex items-center justify-center rounded-lg text-white hover:bg-white/10 transition-colors" data-testid="share-play-btn">
                    {isPlaying ? <Pause size={22} weight="fill" /> : <Play size={22} weight="fill" />}
                  </button>
                  <button onClick={() => skip(-10)} className="w-8 h-8 flex items-center justify-center rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors"><SkipBack size={18} /></button>
                  <button onClick={() => skip(10)} className="w-8 h-8 flex items-center justify-center rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors"><SkipForward size={18} /></button>
                  <button onClick={toggleMute} className="w-8 h-8 flex items-center justify-center rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors">
                    {isMuted ? <SpeakerSlash size={18} /> : <SpeakerHigh size={18} />}
                  </button>
                  <div className="text-xs text-white/70 font-mono ml-2">{formatTime(currentTime)} <span className="text-white/40">/</span> {formatTime(duration)}</div>
                  <div className="flex-1" />
                  <div className="relative">
                    <button onClick={() => setShowSpeedMenu(!showSpeedMenu)} className="h-7 px-2 flex items-center gap-1 rounded-md text-xs text-white/70 hover:text-white hover:bg-white/10 font-medium">
                      <Gauge size={14} />{playbackSpeed}x
                    </button>
                    {showSpeedMenu && (
                      <div className="absolute bottom-full right-0 mb-2 bg-zinc-900/95 backdrop-blur-sm border border-zinc-700 rounded-lg overflow-hidden shadow-xl min-w-[80px]">
                        {[0.5, 0.75, 1, 1.25, 1.5, 2].map(s => (
                          <button key={s} onClick={() => { if (videoRef.current) videoRef.current.playbackRate = s; setPlaybackSpeed(s); setShowSpeedMenu(false); }}
                            className={`w-full px-3 py-2 text-xs text-left hover:bg-white/10 ${playbackSpeed === s ? "text-blue-400 font-semibold" : "text-white/80"}`}>{s}x</button>
                        ))}
                      </div>
                    )}
                  </div>
                  <button onClick={() => { if (document.fullscreenElement) document.exitFullscreen(); else playerRef.current?.requestFullscreen(); }}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors">
                    <CornersOut size={18} />
                  </button>
                </div>
              </div>
            </div>

            {/* Video Info */}
            <div>
              <h1 className="text-xl font-bold text-white font-['Outfit']" data-testid="shared-video-title">{video.title}</h1>
              <div className="flex items-center gap-3 mt-1.5 text-sm text-zinc-400">
                {video.training_theme && <span className="capitalize">{video.training_theme}</span>}
                {video.created_at && (
                  <>
                    <span className="text-zinc-600">|</span>
                    <span>{new Date(video.created_at).toLocaleDateString("fr-FR")}</span>
                  </>
                )}
                {video.ai_generated && (
                  <>
                    <span className="text-zinc-600">|</span>
                    <span className="text-blue-400 text-xs font-medium px-2 py-0.5 bg-blue-500/10 rounded-full">IA</span>
                  </>
                )}
              </div>
              {video.description && <p className="text-zinc-400 text-sm mt-3 leading-relaxed">{video.description}</p>}
            </div>

            {/* Pipeline IA */}
            {pipeline && (pipeline.knowledge_count > 0 || pipeline.segments_count > 0) && (
              <div className="mt-4 bg-zinc-900/50 border border-zinc-800 rounded-lg p-4" data-testid="shared-pipeline">
                <h2 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-3 flex items-center gap-2">Pipeline IA <img src={KNORIA_LOGO_URL} alt="KNORIA" className="h-3.5 inline-block object-contain" /></h2>
                <div className="flex items-center gap-0 mb-4" data-testid="pipeline-steps">
                  {[
                    { icon: Waveform, label: "Audio", color: "#3b82f6" },
                    { icon: FileText, label: "Transcription", color: "#22c55e" },
                    { icon: MicrophoneStage, label: "Diarisation", color: "#eab308" },
                    { icon: Brain, label: "Extraction IA", color: "#a855f7" },
                    { icon: Translate, label: "Embeddings", color: "#ec4899" },
                  ].map((step, i, arr) => (
                    <div key={step.label} className="flex items-center flex-1">
                      <div className="flex flex-col items-center flex-1">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center border-2"
                          style={{ borderColor: step.color, backgroundColor: `${step.color}15` }}>
                          <step.icon size={14} style={{ color: step.color }} />
                        </div>
                        <span className="text-[9px] text-zinc-500 mt-1 text-center leading-tight">{step.label}</span>
                        <CheckCircle size={10} className="text-teal-400 mt-0.5" weight="fill" />
                      </div>
                      {i < arr.length - 1 && <div className="h-0.5 flex-1 bg-teal-500/30 -mt-4 mx-0.5" />}
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-4 gap-2" data-testid="pipeline-stats">
                  {pipeline.segments_count > 0 && (
                    <div className="bg-zinc-800/60 rounded-md px-3 py-2">
                      <div className="text-lg font-bold text-white">{pipeline.segments_count}</div>
                      <div className="text-[10px] text-zinc-500">Segments</div>
                    </div>
                  )}
                  {pipeline.knowledge_count > 0 && (
                    <div className="bg-zinc-800/60 rounded-md px-3 py-2">
                      <div className="text-lg font-bold text-white">{pipeline.knowledge_count}</div>
                      <div className="text-[10px] text-zinc-500">Connaissances</div>
                    </div>
                  )}
                  {pipeline.speakers_count > 0 && (
                    <div className="bg-zinc-800/60 rounded-md px-3 py-2">
                      <div className="text-lg font-bold text-white">{pipeline.speakers_count}</div>
                      <div className="text-[10px] text-zinc-500">Intervenants</div>
                    </div>
                  )}
                  {pipeline.word_count > 0 && (
                    <div className="bg-zinc-800/60 rounded-md px-3 py-2">
                      <div className="text-lg font-bold text-white">{pipeline.word_count.toLocaleString("fr-FR")}</div>
                      <div className="text-[10px] text-zinc-500">Mots</div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Side Panel (2/5) — Transcript + Knowledge */}
          <div className="xl:col-span-2">
            <div className="bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden sticky top-16">
              {/* Tabs */}
              <div className="flex border-b border-zinc-800">
                <button
                  onClick={() => setActivePanel("transcript")}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors relative ${activePanel === "transcript" ? "text-blue-400" : "text-zinc-500 hover:text-zinc-300"}`}
                  data-testid="shared-tab-transcript"
                >
                  <FileText size={16} />
                  Transcript
                  {segments?.length > 0 && (
                    <span className="ml-1 text-[10px] bg-blue-500/10 text-blue-400 px-1.5 py-0.5 rounded-full font-semibold">{segments.length}</span>
                  )}
                  {activePanel === "transcript" && <div className="absolute bottom-0 left-2 right-2 h-0.5 bg-blue-500 rounded-full" />}
                </button>
                <button
                  onClick={() => setActivePanel("knowledge")}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors relative ${activePanel === "knowledge" ? "text-blue-400" : "text-zinc-500 hover:text-zinc-300"}`}
                  data-testid="shared-tab-knowledge"
                >
                  <Brain size={16} />
                  Connaissances
                  {knowledge_units?.length > 0 && (
                    <span className="ml-1 text-[10px] bg-purple-500/10 text-purple-400 px-1.5 py-0.5 rounded-full font-semibold">{knowledge_units.length}</span>
                  )}
                  {activePanel === "knowledge" && <div className="absolute bottom-0 left-2 right-2 h-0.5 bg-blue-500 rounded-full" />}
                </button>
              </div>

              {/* Transcript Panel */}
              {activePanel === "transcript" && (
                <div>
                  {/* Search */}
                  <div className="p-3 border-b border-zinc-800">
                    <div className="relative">
                      <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
                      <input
                        type="text"
                        placeholder="Rechercher dans le transcript..."
                        value={transcriptSearch}
                        onChange={(e) => setTranscriptSearch(e.target.value)}
                        className="w-full h-8 pl-8 pr-8 text-sm bg-zinc-800 border border-zinc-700 rounded-md text-zinc-200 placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        data-testid="shared-transcript-search"
                      />
                      {transcriptSearch && (
                        <button onClick={() => setTranscriptSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300">
                          <X size={14} />
                        </button>
                      )}
                    </div>
                    {transcriptSearch && (
                      <p className="text-[11px] text-zinc-500 mt-1.5">{filteredSegments.length} resultat{filteredSegments.length !== 1 ? "s" : ""}</p>
                    )}
                  </div>

                  {/* Speaker Legend */}
                  {Object.keys(speakerMap).length > 1 && (
                    <div className="px-3 py-2 border-b border-zinc-800 flex flex-wrap gap-2">
                      {Object.entries(speakerMap).map(([speaker, color]) => (
                        <span key={speaker} className="flex items-center gap-1.5 text-[11px] font-medium" style={{ color }}>
                          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
                          {speaker}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Segments List */}
                  <div ref={transcriptContainerRef} className="max-h-[calc(100vh-220px)] min-h-[300px] overflow-y-auto scroll-smooth" data-testid="shared-transcript-container">
                    {filteredSegments.length > 0 ? (
                      <div>
                        {filteredSegments.map((segment, idx) => {
                          const realIdx = segments.indexOf(segment);
                          const isActive = realIdx === activeIdx;
                          const prevSpeaker = realIdx > 0 ? segments[realIdx - 1]?.speaker : null;
                          const showSpeaker = segment.speaker && segment.speaker !== prevSpeaker;
                          const speakerColor = speakerMap[segment.speaker] || "#71717a";

                          return (
                            <div key={realIdx} ref={isActive ? activeSegmentRef : null}>
                              {showSpeaker && (
                                <div className="px-3 py-1.5 flex items-center gap-2" style={{ backgroundColor: `${speakerColor}08` }}>
                                  <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: speakerColor }} />
                                  <span className="text-xs font-semibold" style={{ color: speakerColor }}>{segment.speaker}</span>
                                </div>
                              )}
                              <div
                                className={`px-3 py-2.5 cursor-pointer transition-all duration-200 border-l-2 ${
                                  isActive ? "bg-blue-500/10 border-blue-500" : "border-transparent hover:bg-zinc-800/50"
                                }`}
                                onClick={() => seekTo(segment.start)}
                                data-testid={`shared-segment-${realIdx}`}
                              >
                                <div className="flex items-start gap-2">
                                  <button
                                    className={`flex-shrink-0 text-[11px] font-mono px-1.5 py-0.5 rounded transition-colors ${
                                      isActive ? "bg-blue-500 text-white" : "text-zinc-500 bg-zinc-800 hover:bg-blue-500/20 hover:text-blue-400"
                                    }`}
                                    onClick={(e) => { e.stopPropagation(); seekTo(segment.start); }}
                                  >
                                    {formatTime(segment.start)}
                                  </button>
                                  <p className={`text-sm leading-relaxed flex-1 ${isActive ? "text-zinc-100 font-medium" : "text-zinc-400"}`}>
                                    {transcriptSearch ? highlightText(segment.text, transcriptSearch) : segment.text}
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="p-10 text-center text-zinc-500">
                        <FileText size={36} className="mx-auto mb-3 opacity-30" />
                        <p className="text-sm">{transcriptSearch ? "Aucun résultat" : "Aucun transcript"}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Knowledge Panel */}
              {activePanel === "knowledge" && (
                <div className="max-h-[calc(100vh-180px)] min-h-[300px] overflow-y-auto" data-testid="shared-knowledge-container">
                  {knowledge_units?.length > 0 ? (
                    <div className="p-3 space-y-2">
                      {knowledge_units.map((unit) => {
                        const Icon = TYPE_ICONS[unit.type] || Lightbulb;
                        const colors = TYPE_COLORS[unit.type] || TYPE_COLORS.concept;
                        return (
                          <div
                            key={unit.id}
                            className="p-3 rounded-lg border cursor-pointer transition-all duration-200 hover:shadow-md hover:-translate-y-0.5"
                            style={{ backgroundColor: colors.bg, borderColor: colors.border }}
                            onClick={() => unit.timestamps?.start != null && seekTo(unit.timestamps.start)}
                            data-testid={`shared-knowledge-${unit.id}`}
                          >
                            <div className="flex items-start gap-2.5">
                              <div className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${colors.color}20` }}>
                                <Icon size={14} style={{ color: colors.color }} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-0.5">
                                  <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: colors.color }}>{unit.type}</span>
                                  {unit.timestamps?.start != null && (
                                    <span className="text-[10px] text-zinc-500 font-mono">{formatTime(unit.timestamps.start)}</span>
                                  )}
                                </div>
                                <h4 className="text-sm font-medium text-zinc-100 line-clamp-2 leading-snug">{unit.title || "Sans titre"}</h4>
                                {unit.content?.definition && <p className="text-xs text-zinc-400 mt-1 line-clamp-2 leading-relaxed">{unit.content.definition}</p>}
                                {unit.content?.question && <p className="text-xs text-zinc-400 mt-1 line-clamp-2 leading-relaxed">Q: {unit.content.question}</p>}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="p-10 text-center text-zinc-500">
                      <Brain size={36} className="mx-auto mb-3 opacity-30" />
                      <p className="text-sm">Aucune connaissance extraite</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-10 pt-6 border-t border-zinc-800 text-center">
          <p className="text-zinc-500 text-xs">
            Partage via <img src={KNORIA_LOGO_URL} alt="KNORIA" className="h-4 inline-block mx-1 object-contain" /> — Knowledge Empowered
          </p>
        </div>
      </div>
    </div>
  );
}

function highlightText(text, query) {
  if (!query) return text;
  const parts = text.split(new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, "gi"));
  return parts.map((part, i) =>
    part.toLowerCase() === query.toLowerCase() ? (
      <mark key={`hl-${i}-${part.length}`} className="bg-yellow-500/30 text-yellow-200 rounded px-0.5">{part}</mark>
    ) : part
  );
}
